// 🎬 VideoGrid.js - MODALITÀ PRESENTAZIONE SENZA TAGLI
// ✅ object-fit: contain per schermo COMPLETO
// ✅ NO max-width/max-height restrictions

import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const GridContainer = styled.div`
  width: 100%;
  height: 100%;
  display: ${props => props.$presentationMode ? 'flex' : 'grid'};
  flex-direction: ${props => props.$presentationMode ? 'column' : 'row'};
  gap: ${props => props.$presentationMode ? '0' : '8px'};
  padding: ${props => props.$presentationMode ? '0' : '1rem'};
  grid-template-columns: ${props => props.gridcols};
  grid-template-rows: ${props => props.gridrows};
  background: #0f1419;
`;

// ✅ SCHERMO GRANDE - Flex: 1 = occupa tutto lo spazio
const PresentationScreen = styled.div`
  flex: 1;
  width: 100%;
  min-height: 0; /* ✅ CRITICO per flex */
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

// ✅ STRIP WEBCAM - Altezza fissa 150px
const ParticipantsStrip = styled.div`
  height: 150px;
  width: 100%;
  background: rgba(0, 0, 0, 0.95);
  border-top: 3px solid rgba(255, 107, 53, 0.7);
  padding: 0.75rem;
  overflow-x: auto;
  overflow-y: hidden;
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
`;

// ✅ MINI VIDEO CARD
const MiniVideoCard = styled(motion.div)`
  position: relative;
  background: ${props => props.islocal === 'true' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(0, 0, 0, 0.8)'};
  border-radius: 10px;
  overflow: hidden;
  border: ${props => props.islocal === 'true' ? '3px solid #10b981' : '2px solid rgba(255, 255, 255, 0.2)'};
  min-width: 130px;
  width: 130px;
  height: 100%;
  flex-shrink: 0;
`;

// ✅ VIDEO ELEMENT - object-fit: contain = NESSUN TAGLIO
const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: ${props => props.$isSharing ? 'contain' : 'cover'}; /* ✅ CONTAIN per schermo */
  background: ${props => props.$isSharing ? '#000' : 'transparent'};
  display: ${props => props.$hidden ? 'none' : 'block'};
`;

// ✅ VIDEO CARD NORMALE
const VideoCard = styled(motion.div)`
  position: relative;
  background: ${props => props.$isSharing ? '#000' : 'linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(26, 26, 46, 0.8))'};
  border-radius: 12px;
  overflow: hidden;
  border: ${props => props.$isLocal ? '2px solid #10b981' : props.$isSharing ? '2px solid #FF6B35' : '1px solid rgba(255, 255, 255, 0.1)'};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const VideoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${props => props.$isLocal ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255, 107, 53, 0.1)'};
  color: rgba(255, 255, 255, 0.6);
  font-size: ${props => props.$mini ? '1.5rem' : '3rem'};
`;

const AvatarText = styled.div`
  font-size: ${props => props.$mini ? '0.7rem' : '1rem'};
  margin-top: ${props => props.$mini ? '0.2rem' : '0.5rem'};
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
`;

const UserInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.9));
  padding: ${props => props.$mini ? '0.5rem' : '1rem'};
  color: white;
`;

const UserName = styled.div`
  font-size: ${props => props.$mini ? '0.75rem' : '0.9rem'};
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AudioIndicator = styled.div`
  position: absolute;
  top: ${props => props.$mini ? '8px' : '12px'};
  right: ${props => props.$mini ? '8px' : '12px'};
  background: ${props => props.$muted ? 'rgba(239, 68, 68, 0.95)' : 'rgba(16, 185, 129, 0.95)'};
  color: white;
  padding: ${props => props.$mini ? '0.35rem 0.5rem' : '0.5rem 0.7rem'};
  border-radius: 8px;
  font-size: ${props => props.$mini ? '0.75rem' : '0.85rem'};
  font-weight: 700;
`;

const VideoIndicator = styled.div`
  position: absolute;
  top: ${props => props.$mini ? '8px' : '12px'};
  left: ${props => props.$mini ? '8px' : '12px'};
  background: ${props => props.$disabled ? 'rgba(239, 68, 68, 0.95)' : 'rgba(16, 185, 129, 0.95)'};
  color: white;
  padding: ${props => props.$mini ? '0.35rem 0.5rem' : '0.4rem 0.6rem'};
  border-radius: 8px;
  font-size: ${props => props.$mini ? '0.7rem' : '0.75rem'};
  font-weight: 700;
`;

const ScreenShareBadge = styled.div`
  position: absolute;
  top: ${props => props.$mini ? '45px' : '60px'};
  left: ${props => props.$mini ? '8px' : '12px'};
  background: rgba(255, 107, 53, 0.95);
  color: white;
  padding: ${props => props.$mini ? '0.4rem 0.6rem' : '0.6rem 1.2rem'};
  border-radius: 10px;
  font-size: ${props => props.$mini ? '0.7rem' : '0.9rem'};
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const InfoBadge = styled(motion.div)`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(16, 185, 129, 0.95);
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 700;
  z-index: 100;
  pointer-events: none;
`;

function VideoGrid({ localStream, remoteStreams, participants }) {
  const videoRefs = useRef({});
  const [, forceUpdate] = useState({});
  const [showInfoBadge, setShowInfoBadge] = useState(false);

  const allParticipants = localStream ? [
    {
      id: 'local',
      displayName: 'Tu',
      role: 'host',
      isLocal: true,
      videoEnabled: localStream.getVideoTracks()[0]?.enabled ?? false,
      audioEnabled: localStream.getAudioTracks()[0]?.enabled ?? false,
      isSharing: false
    },
    ...participants
  ] : participants;

  const presentationMode = allParticipants.some(p => p.isSharing === true);
  
  useEffect(() => {
    if (presentationMode) {
      setShowInfoBadge(true);
      const timer = setTimeout(() => setShowInfoBadge(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [presentationMode]);

  const sharingParticipant = allParticipants.find(p => p.isSharing === true);
  const stripParticipants = allParticipants.filter(p => p.isSharing !== true);

  useEffect(() => {
    Object.keys(videoRefs.current).forEach(id => {
      const videoElement = videoRefs.current[id];
      if (videoElement) {
        const stream = id === 'local' ? localStream : remoteStreams[id];
        if (stream && videoElement.srcObject !== stream) {
          videoElement.srcObject = stream;
        }
      }
    });
  }, [localStream, remoteStreams, allParticipants]);

  useEffect(() => {
    if (!localStream) return;
    const handleTrackChange = () => forceUpdate({});
    const videoTrack = localStream.getVideoTracks()[0];
    const audioTrack = localStream.getAudioTracks()[0];
    if (videoTrack) {
      videoTrack.addEventListener('ended', handleTrackChange);
      videoTrack.addEventListener('mute', handleTrackChange);
      videoTrack.addEventListener('unmute', handleTrackChange);
    }
    if (audioTrack) {
      audioTrack.addEventListener('ended', handleTrackChange);
      audioTrack.addEventListener('mute', handleTrackChange);
      audioTrack.addEventListener('unmute', handleTrackChange);
    }
    return () => {
      if (videoTrack) {
        videoTrack.removeEventListener('ended', handleTrackChange);
        videoTrack.removeEventListener('mute', handleTrackChange);
        videoTrack.removeEventListener('unmute', handleTrackChange);
      }
      if (audioTrack) {
        audioTrack.removeEventListener('ended', handleTrackChange);
        audioTrack.removeEventListener('mute', handleTrackChange);
        audioTrack.removeEventListener('unmute', handleTrackChange);
      }
    };
  }, [localStream]);

  function getGridLayout(count) {
    if (count === 1) return { cols: '1fr', rows: '1fr' };
    if (count === 2) return { cols: 'repeat(2, 1fr)', rows: '1fr' };
    if (count <= 4) return { cols: 'repeat(2, 1fr)', rows: 'repeat(2, 1fr)' };
    if (count <= 6) return { cols: 'repeat(3, 1fr)', rows: 'repeat(2, 1fr)' };
    if (count <= 9) return { cols: 'repeat(3, 1fr)', rows: 'repeat(3, 1fr)' };
    return { cols: 'repeat(4, 1fr)', rows: 'repeat(3, 1fr)' };
  }

  const renderPresentationVideo = (p) => {
    const stream = p.id === 'local' ? localStream : remoteStreams[p.id];
    const hasVideo = stream && stream.getVideoTracks().length > 0;
    const videoEnabled = hasVideo && stream.getVideoTracks()[0].enabled;
    const hasAudio = stream && stream.getAudioTracks().length > 0;
    const audioEnabled = hasAudio && stream.getAudioTracks()[0].enabled;

    return (
      <>
        <Video
          ref={el => { if (el) videoRefs.current[p.id] = el; }}
          autoPlay
          playsInline
          muted={p.isLocal}
          $hidden={!videoEnabled}
          $isSharing={true} /* ✅ ATTIVA object-fit: contain */
        />

        {!videoEnabled && (
          <VideoPlaceholder $isLocal={p.isLocal}>
            <div style={{ fontSize: '5rem' }}>🖥️</div>
            <AvatarText style={{ fontSize: '1.5rem', marginTop: '1.5rem' }}>
              {p.displayName}
            </AvatarText>
          </VideoPlaceholder>
        )}

        <ScreenShareBadge>
          🖥️ {p.displayName} sta condividendo
        </ScreenShareBadge>

        <AudioIndicator $muted={!audioEnabled}>
          {audioEnabled ? '🎤 ON' : '🔇'}
        </AudioIndicator>
      </>
    );
  };

  const renderParticipant = (p, isMini = false) => {
    const stream = p.id === 'local' ? localStream : remoteStreams[p.id];
    const hasVideo = stream && stream.getVideoTracks().length > 0;
    const videoEnabled = hasVideo && stream.getVideoTracks()[0].enabled;
    const hasAudio = stream && stream.getAudioTracks().length > 0;
    const audioEnabled = hasAudio && stream.getAudioTracks()[0].enabled;

    const CardComponent = isMini ? MiniVideoCard : VideoCard;

    return (
      <CardComponent
        key={p.id}
        islocal={p.isLocal ? 'true' : undefined}
        $isLocal={p.isLocal}
        $isSharing={p.isSharing}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
      >
        <Video
          ref={el => { if (el) videoRefs.current[p.id] = el; }}
          autoPlay
          playsInline
          muted={p.isLocal}
          $hidden={!videoEnabled}
          $isSharing={p.isSharing}
        />

        {!videoEnabled && (
          <VideoPlaceholder $isLocal={p.isLocal} $mini={isMini}>
            <div>{p.isLocal ? '🟢' : p.role === 'host' ? '👑' : '👤'}</div>
            <AvatarText $mini={isMini}>{p.displayName}</AvatarText>
          </VideoPlaceholder>
        )}

        <VideoIndicator $disabled={!videoEnabled} $mini={isMini}>
          {videoEnabled ? '📹' : '📷'}
        </VideoIndicator>

        <AudioIndicator $muted={!audioEnabled} $mini={isMini}>
          {audioEnabled ? '🎤' : '🔇'}
        </AudioIndicator>

        <UserInfo $mini={isMini}>
          <UserName $mini={isMini}>
            {p.role === 'host' ? '👑 ' : '👤 '}
            {p.displayName}
            {p.isLocal && ' (Tu)'}
          </UserName>
        </UserInfo>
      </CardComponent>
    );
  };

  // ✅ MODALITÀ PRESENTAZIONE - Schermo GRANDE senza tagli
  if (presentationMode) {
    return (
      <GridContainer $presentationMode={true}>
        <PresentationScreen>
          {renderPresentationVideo(sharingParticipant)}
          
          {showInfoBadge && (
            <InfoBadge
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              🖥️ Modalità Presentazione
            </InfoBadge>
          )}
        </PresentationScreen>

        <ParticipantsStrip>
          {stripParticipants.map(p => renderParticipant(p, true))}
        </ParticipantsStrip>
      </GridContainer>
    );
  }

  // Modalità griglia normale
  const { cols, rows } = getGridLayout(allParticipants.length);
  return (
    <GridContainer gridcols={cols} gridrows={rows} $presentationMode={false}>
      {allParticipants.map(p => renderParticipant(p))}
    </GridContainer>
  );
}

export default VideoGrid;
